<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'

const open = defineModel<boolean>('open', { required: true })

const props = withDefaults(
  defineProps<{
    title?: string
    /** Panel max-width preset */
    size?: 'md' | 'lg' | 'xl'
    /** When false, Escape and backdrop clicks no longer close the panel. */
    dismissible?: boolean
  }>(),
  { size: 'md', title: '', dismissible: true },
)

const sizeClass: Record<'md' | 'lg' | 'xl', string> = {
  md: 'max-w-lg',
  lg: 'max-w-xl',
  xl: 'max-w-2xl',
}

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.dismissible) open.value = false
}

function onBackdrop() {
  if (props.dismissible) open.value = false
}

watch(
  () => open.value,
  (v) => {
    if (v) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
  },
)

onMounted(() => document.addEventListener('keydown', onKey))
onUnmounted(() => {
  document.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? 'dialog-title' : undefined"
      >
        <div
          class="absolute inset-0 bg-black/70 backdrop-blur-sm"
          @click="onBackdrop"
        />
        <div
          class="relative z-10 w-full rounded-2xl border border-white/10 bg-surface-900 p-5 shadow-glass sm:p-6"
          :class="sizeClass[props.size]"
        >
          <h2 v-if="title" id="dialog-title" class="text-lg font-semibold text-slate-100">
            {{ title }}
          </h2>
          <div :class="title ? 'mt-3' : ''">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>
